import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import * as Draft from "src/scenes/orgAdmin/formTemplates/types/draft";
import * as AF from "src/types/formTemplate";
import * as AFV from "src/types/formTemplate/visibility";
import { z } from "zod";

export function toFormValue(
  question: Draft.Question,
  isVisibilityAdminFlagEnabled: boolean
): FormikType {
  const visibility = question.visibility;
  if (visibility === undefined || visibility === null) {
    return EmptyFormValue;
  }

  if (!isVisibilityAdminFlagEnabled) {
    return {
      questionVisibilityJSON: JSON.stringify(visibility, null, 2),
      questionVisibility: {
        switch: "disabled",
        logicalOperator: undefined,
        rules: [],
      },
    };
  }

  return {
    questionVisibility: {
      switch: "checked",
      logicalOperator: toFormLogicalOperator(visibility),
      rules: toFormRules(visibility),
    },
  };
}

function toFormRules(
  visibility: AFV.Visibility
): FormikType["questionVisibility"]["rules"] {
  const rules = visibility.allOf ?? visibility.anyOf;
  if (rules === undefined) {
    throw new Error("Unsupported visibility configuration");
  }

  return rules.map((config) => {
    const rule = config.rules[0];
    if (rule === undefined) {
      throw new Error("Missing rule");
    }

    if (config.rules.length > 1) {
      throw new Error("Multiple rules are not supported");
    }

    switch (rule.dataType) {
      case "questionKey": {
        return {
          questionKey: rule.property,
          optionKeys: Array.isArray(rule.value) ? rule.value : [rule.value],
        };
      }
      case "school": {
        return {
          questionKey: SchoolSelectionItem.key,
          optionKeys: Array.isArray(rule.value) ? rule.value : [rule.value],
        };
      }
      default: {
        const _exhaustiveCheck: never = rule;
        throw new Error("Unhandled rule: " + rule);
      }
    }
  });
}

function toFormLogicalOperator(visibility: AFV.Visibility): LogicalOperator {
  if (visibility.allOf !== undefined) {
    return "and";
  }

  if (visibility.anyOf !== undefined) {
    return "or";
  }

  throw new Error("Unsupported logical operator");
}

export function fromFormValue(formValue: FormikType): AFV.Visibility | null {
  if (formValue.questionVisibility.switch !== "checked") {
    return null;
  }

  const base = {
    default: "HIDDEN",
    result: "VISIBLE",
  } as const;

  switch (formValue.questionVisibility.logicalOperator) {
    case "and": {
      return {
        ...base,
        allOf: formValue.questionVisibility.rules.flatMap(toRule),
      };
    }
    case "or": {
      return {
        ...base,
        anyOf: formValue.questionVisibility.rules.flatMap(toRule),
      };
    }
    default: {
      const _exhaustiveCheck: never = formValue.questionVisibility;
      throw new Error(`Unhandled case for ${formValue.questionVisibility}`);
    }
  }
}

function toRule(rule: RuleType | LastRuleType): AFV.Rules[] {
  const result = RuleSchema.safeParse(rule);
  if (!result.success) {
    // ignore invalid rule
    return [];
  }

  if (result.data.questionKey === SchoolSelectionItem.key) {
    // School rule
    return [
      {
        rules: [
          {
            dataType: "school",
            operator: "IN",
            value: result.data.optionKeys,
          },
        ],
      },
    ];
  }

  // Question rule
  return [
    {
      rules: [
        {
          dataType: "questionKey",
          property: result.data.questionKey,
          operator: "IN",
          value: result.data.optionKeys,
        },
      ],
    },
  ];
}

export const RuleSchema = z
  .object({
    questionKey: z.string({
      message: "Choosing a question for the conditional reveal is required.",
    }),
    optionKeys: z.array(z.string()),
  })
  .superRefine((rule, ctx) => {
    if (rule.optionKeys.length < 1) {
      if (rule.questionKey === SchoolSelectionItem.key) {
        ctx.addIssue({
          code: "custom",
          path: ["optionKeys"],
          message: "At least one school is required",
        });
      } else {
        ctx.addIssue({
          code: "custom",
          path: ["optionKeys"],
          message: "At least one answer is required",
        });
      }
    }
  });

export const LastRuleSchema = z
  .object({
    questionKey: z.string().optional(), // Allows undefined
    optionKeys: z.array(z.string()), // Allows empty array
  })
  .superRefine((rule, ctx) => {
    if (rule.questionKey === undefined || rule.questionKey === "") {
      return;
    }

    if (rule.optionKeys.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["optionKeys"],
        message: "At least one answer is required",
      });
      return;
    }
  });

type RuleType = z.infer<typeof RuleSchema>;
type LastRuleType = z.infer<typeof LastRuleSchema>;

const LogicalOperatorSchema = z.union([z.literal("and"), z.literal("or")]);
type LogicalOperator = z.infer<typeof LogicalOperatorSchema>;

export const FormikSchema = z.object({
  questionVisibilityJSON: z.string().optional(),
  questionVisibility: z.discriminatedUnion("switch", [
    z.object({
      switch: z.literal("disabled"),
      logicalOperator: z.any(),
      rules: z.array(z.any()),
    }),
    z.object({
      switch: z.literal("checked"),
      logicalOperator: LogicalOperatorSchema,

      // Special validation for rules array
      rules: z
        .array(z.union([RuleSchema, LastRuleSchema]))
        .superRefine((rules, ctx) => {
          // If there are no rules, it's valid
          if (rules.length === 0) {
            ctx.addIssue({
              code: "custom",
              message: "At least one rule is required",
              path: ["questionVisibility", "rules"],
            });
            return;
          }

          if (rules.length === 1) {
            const result = RuleSchema.safeParse(rules[0]);
            result.error?.issues.forEach((issue) =>
              ctx.addIssue({ ...issue, path: [0, ...issue.path] })
            );
            return;
          }

          const lastIndex = rules.length - 1;

          // For all rules except the last one, they must be valid according to RuleSchema
          for (let i = 0; i < lastIndex; i++) {
            const rule = rules[i];
            const result = RuleSchema.safeParse(rule);
            result.error?.issues.forEach((issue) =>
              ctx.addIssue({ ...issue, path: [i, ...issue.path] })
            );
          }

          // The last rule can either be a complete rule or an incomplete rule
          const lastRule = rules[lastIndex];
          const result = LastRuleSchema.safeParse(lastRule);
          result.error?.issues.forEach((issue) =>
            ctx.addIssue({ ...issue, path: [lastIndex, ...issue.path] })
          );
          return;
        }),
    }),
  ]),
});
export type FormikType = z.infer<typeof FormikSchema>;

export const NewRule: RuleType = {
  questionKey: "",
  optionKeys: [],
};

export const EmptyFormValue: FormikType = {
  questionVisibilityJSON: "",
  questionVisibility: {
    switch: "disabled",
    logicalOperator: "and",
    rules: [NewRule],
  },
};

export function findRelatedQuestionVisibilityByAnswerId(
  questions: readonly Draft.Question[],
  questionId: string,
  answerId: string
): Draft.Question[] {
  const relatedQuestions: Draft.Question[] = [];

  // Loop through all questions to find those with visibility rules dependent on the target question
  for (const question of questions) {
    // Skip the question if it doesn't have visibility rules
    if (!question.visibility) continue;

    let isRelated = false;

    // Check allOf rules
    if (question.visibility.allOf) {
      isRelated = question.visibility.allOf.some((rule) => {
        return rule.rules.some((condition) => {
          return (
            condition.dataType === "questionKey" &&
            condition.property === questionId &&
            condition.operator === "IN" &&
            (Array.isArray(condition.value)
              ? condition.value.includes(answerId)
              : condition.value === answerId)
          );
        });
      });
    }

    // Check anyOf rules if not already found to be related
    if (!isRelated && question.visibility.anyOf) {
      isRelated = question.visibility.anyOf.some((rule) => {
        return rule.rules.some((condition) => {
          return (
            condition.dataType === "questionKey" &&
            condition.property === questionId &&
            condition.operator === "IN" &&
            (Array.isArray(condition.value)
              ? condition.value.includes(answerId)
              : condition.value === answerId)
          );
        });
      });
    }

    // If the question has a related visibility rule, add it to the result array
    if (isRelated) {
      relatedQuestions.push(question);
    }
  }

  return relatedQuestions;
}

export function findRelatedQuestionVisibilityByQuestionId(
  questions: readonly Draft.Question[],
  questionId: string
): Draft.Question[] {
  const relatedQuestions: Draft.Question[] = [];

  // Loop through all questions to find those with visibility rules dependent on the target question
  for (const question of questions) {
    // Skip the question if it doesn't have visibility rules
    if (!question.visibility) continue;

    let isRelated = false;

    // Check allOf rules
    if (question.visibility.allOf) {
      isRelated = question.visibility.allOf.some((rule) => {
        return rule.rules.some((condition) => {
          return (
            condition.dataType === "questionKey" &&
            condition.property === questionId
          );
        });
      });
    }

    // Check anyOf rules if not already found to be related
    if (!isRelated && question.visibility.anyOf) {
      isRelated = question.visibility.anyOf.some((rule) => {
        return rule.rules.some((condition) => {
          return (
            condition.dataType === "questionKey" &&
            condition.property === questionId
          );
        });
      });
    }

    // If the question has a related visibility rule, add it to the result array
    if (isRelated) {
      relatedQuestions.push(question);
    }
  }

  return relatedQuestions;
}

export function findInvalidQuestionVisibility(
  questions: readonly AF.Question<AF.WithId>[]
): uuid[] {
  const invalidIds: string[] = [];

  // Get all question ids in the form
  const availableQuestionIds = new Set(
    questions.map((question) => question.id)
  );

  // Create a map of question ids to valid option ids
  const questionOptionsMap = new Map<string, Set<string>>();
  for (const question of questions) {
    if ("options" in question && question.id !== undefined) {
      questionOptionsMap.set(
        question.id,
        new Set(
          question.options.flatMap((option) => (option.id ? [option.id] : []))
        )
      );
    }
  }

  // Iterate through each question to check for invalid visibility rules
  for (const question of questions) {
    // Skip questions without visibility rules
    if (!question.visibility) continue;

    const rules =
      question.visibility.allOf ??
      question.visibility.anyOf ??
      question.visibility.oneOf ??
      [];

    for (const rule of rules) {
      for (const condition of rule.rules) {
        // Check if the condition references a question id
        if (condition.dataType === "questionKey") {
          // Check if the referenced question exists
          if (!availableQuestionIds.has(condition.property)) {
            invalidIds.push(question.id);
            break; // One invalid rule is enough to mark the question
          }

          // Check if the value references valid options
          const validOptions = questionOptionsMap.get(condition.property);
          if (validOptions) {
            const values = Array.isArray(condition.value)
              ? condition.value
              : [condition.value];

            const hasInvalidValue = values.some(
              (value) => !validOptions.has(value)
            );
            if (hasInvalidValue) {
              invalidIds.push(question.id);
              break; // One invalid rule is enough to mark the question
            }
          }
        }
      }
    }
  }

  return invalidIds;
}

const QUESTION_VISIBILITY_TYPE = [
  AF.SingleSelectType,
  AF.MultiSelectType,
  AF.GradesType,
];
export const SchoolSelectionItem: Item = {
  key: "0196b104-83a2-7370-8009-434e2280e499-SCHOOL-SELECTION", // just need any random unique key here
  label: "School selection",
};
export function toQuestionItems(
  sectionType: AF.SectionType,
  questions: readonly Draft.Question[]
): Item[] {
  const items: Item[] =
    sectionType === AF.PreRankingSectionType ? [] : [SchoolSelectionItem];
  for (const question of questions) {
    if (!QUESTION_VISIBILITY_TYPE.includes(question.type)) {
      continue;
    }

    items.push({
      key: question.id,
      label: question.question,
    });
  }

  return items;
}

type Option = {
  id: string;
  label: string;
};
type SchoolItem = {
  id: string;
  label: string;
};
export function toAnswerOptions(
  selectedKey: string | undefined,
  questions: readonly Draft.Question[],
  schools: SchoolItem[]
): Option[] | null {
  if (selectedKey === undefined) {
    return null;
  }

  if (selectedKey === SchoolSelectionItem.key) {
    return schools.map((school) => ({
      id: school.id,
      label: school.label,
    }));
  }

  const selectedQuestion = questions.find((q) => q.id === selectedKey) ?? null;
  if (selectedQuestion === null) {
    return null;
  }

  if (!("options" in selectedQuestion)) {
    return null;
  }

  const options: Option[] = [];
  for (const option of selectedQuestion.options) {
    if (option.id === undefined) {
      continue;
    }
    options.push({
      id: option.id,
      label: option.label,
    });
  }

  return options;
}
